import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../../components/admin/AdminHeader';
import authService from '../../../services/authService';

const ReferralManagementPage = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Partner selection for super admins
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  
  // Track if settings came from database or are just defaults
  const [hasSettingsInDb, setHasSettingsInDb] = useState(false);
  const [settingsModified, setSettingsModified] = useState(false);
  
  // Default settings template
  const defaultSettings = {
    limits: {
      totalReferrals: 0,
      monthlyReferrals: 0,
      concurrentReferrals: 0
    },
    points: {
      referrerSignupPoints: 30,
      referredSignupPoints: 10,
      expirationDays: 365
    },
    nodeTypes: [
      { id: 'peer', label: 'Peer', description: 'Equal relationship', isDefault: true },
      { id: 'customer', label: 'Customer', description: 'Customer relationship', isDefault: false },
      { id: 'friend', label: 'Friend', description: 'Personal friend', isDefault: false }
    ],
    redirectUrl: '',
    webhookUrl: '',
    webhookSecret: '',
    ssrUrl: '',
    enableProxy: true, // Default to true for backward compatibility
    enableNetworkVisualization: false,
    enableNodeTypes: true,
    enableNetworks: false
  };
  
  // Settings state - starts with defaults
  const [settings, setSettings] = useState(defaultSettings);
  
  // Stats state
  const [stats, setStats] = useState(null);
  const [recentReferrals, setRecentReferrals] = useState([]);
  
  useEffect(() => {
    if (!authService.isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    
    const userData = authService.getAdminUser();
    if (!userData) {
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(userData);
    
    // If super admin, load partners list
    if (userData.role === 'super') {
      loadPartners();
    } else {
      // Regular admin - use their partner
      setSelectedPartnerId(userData.partnerId);
      loadData(userData.partnerId);
    }
  }, [navigate]);
  
  // Load data when selected partner changes
  useEffect(() => {
    if (selectedPartnerId) {
      // Warn if there are unsaved changes
      if (settingsModified && !hasSettingsInDb) {
        if (!confirm('You have unsaved changes. The current settings are NOT saved to the database. Continue without saving?')) {
          return;
        }
      }
      loadData(selectedPartnerId);
    }
  }, [selectedPartnerId]);
  
  // Warn before page unload if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (settingsModified && !hasSettingsInDb) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [settingsModified, hasSettingsInDb]);
  
  const loadPartners = async () => {
    try {
      const response = await fetch('/api/admin/partners', {
        headers: {
          'Authorization': `Bearer ${authService.getAdminToken()}`,
          'x-client-token': 'cnxn_admin_token'
        }
      });
      const data = await response.json();
      setPartners(data.partners || []);
      
      // Auto-select first partner if available
      if (data.partners && data.partners.length > 0) {
        setSelectedPartnerId(data.partners[0]._id);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };
  
  const loadData = async (partnerId) => {
    try {
      setLoading(true);
      
      // Load settings
      const settingsRes = await fetch(`/api/admin/referral/settings/${partnerId}`, {
        headers: {
          'Authorization': `Bearer ${authService.getAdminToken()}`,
          'x-client-token': 'cnxn_admin_token',
          'x-partner-id': partnerId
        }
      });
      const settingsData = await settingsRes.json();
      
      console.log('[ReferralManagement] Settings loaded:', settingsData);
      
      // Use the hasSettings flag from backend to determine if settings exist in DB
      if (settingsData.hasSettings && settingsData.settings) {
        // Settings exist in database
        console.log('[ReferralManagement] Settings found in DB');
        setSettings(settingsData.settings);
        setHasSettingsInDb(true);
      } else {
        // No settings in database, show defaults
        console.log('[ReferralManagement] No settings in DB, using defaults');
        setSettings(defaultSettings);
        setHasSettingsInDb(false);
      }
      setSettingsModified(false);
      
      // Load stats
      const statsRes = await fetch(`/api/admin/referral/stats/${partnerId}`, {
        headers: {
          'Authorization': `Bearer ${authService.getAdminToken()}`,
          'x-client-token': 'cnxn_admin_token',
          'x-partner-id': partnerId
        }
      });
      const statsData = await statsRes.json();
      setStats(statsData);
      
      // Load recent referrals
      const referralsRes = await fetch(`/api/admin/referral/recent/${partnerId}?limit=20`, {
        headers: {
          'Authorization': `Bearer ${authService.getAdminToken()}`,
          'x-client-token': 'cnxn_admin_token',
          'x-partner-id': partnerId
        }
      });
      const referralsData = await referralsRes.json();
      setRecentReferrals(referralsData.referrals || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveSettings = async () => {
    if (!selectedPartnerId) return;
    
    try {
      setSaving(true);
      const response = await fetch(`/api/admin/referral/settings/${selectedPartnerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getAdminToken()}`,
          'x-client-token': 'cnxn_admin_token',
          'x-partner-id': selectedPartnerId
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (data.success) {
        setHasSettingsInDb(true);
        setSettingsModified(false);
        alert('Settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleResetToDefaults = () => {
    if (!confirm('Reset to default settings? This will replace current values.')) {
      return;
    }
    
    setSettings(defaultSettings);
    setSettingsModified(true);
  };
  
  const handleLimitChange = (field, value) => {
    if (!settings) return;
    setSettings(prev => ({
      ...prev,
      limits: {
        ...(prev.limits || {}),
        [field]: parseInt(value) || 0
      }
    }));
    setSettingsModified(true);
  };
  
  const handlePointsChange = (field, value) => {
    if (!settings) return;
    setSettings(prev => ({
      ...prev,
      points: {
        ...(prev.points || {}),
        [field]: parseInt(value) || 0
      }
    }));
    setSettingsModified(true);
  };
  
  const handleAddNodeType = () => {
    if (!settings) return;
    
    const id = prompt('Enter node type ID (e.g., "partner", "vendor"):');
    if (!id) return;
    
    const label = prompt('Enter display label:');
    if (!label) return;
    
    const description = prompt('Enter description (optional):') || '';
    
    setSettings(prev => ({
      ...prev,
      nodeTypes: [
        ...(prev.nodeTypes || []),
        { id, label, description, isDefault: false }
      ]
    }));
    setSettingsModified(true);
  };
  
  const handleRemoveNodeType = (index) => {
    if (!settings) return;
    
    if (confirm('Remove this node type?')) {
      setSettings(prev => ({
        ...prev,
        nodeTypes: (prev.nodeTypes || []).filter((_, i) => i !== index)
      }));
      setSettingsModified(true);
    }
  };

  const handleTabChange = (newTab) => {
    // Warn if leaving settings tab with unsaved changes
    if (activeTab === 'settings' && settingsModified && !hasSettingsInDb) {
      if (!confirm('You have unsaved changes. The current settings are NOT saved to the database. Continue without saving?')) {
        return;
      }
    }
    setActiveTab(newTab);
  };

  console.log('[ReferralManagement] Render state:', { hasSettingsInDb, settingsModified, activeTab, selectedPartnerId });

  if (!adminUser || loading) {
    return <div>Loading...</div>;
  }
  
  const tabStyle = {
    padding: '12px 24px',
    cursor: 'pointer',
    borderBottomWidth: '3px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    transition: 'all 0.2s',
    backgroundColor: 'transparent'
  };

  const activeTabStyle = {
    ...tabStyle,
    borderBottomColor: '#4f46e5',
    color: '#4f46e5'
  };
  
  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '28px', 
              color: '#111827' 
            }}>
              Referral Management
            </h2>
            <p style={{ 
              margin: '0', 
              fontSize: '14px', 
              color: '#6b7280' 
            }}>
              Configure referral limits, points rewards, and network settings
            </p>
          </div>
          
          {/* Partner Selector for Super Admin */}
          {adminUser.role === 'super' && partners.length > 0 && (
            <div style={{ minWidth: '250px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#6b7280',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Select Partner
              </label>
              <select
                value={selectedPartnerId}
                onChange={(e) => setSelectedPartnerId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#111827',
                  cursor: 'pointer'
                }}
              >
                {partners.map(partner => (
                  <option key={partner._id} value={partner._id}>
                    {partner.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {!selectedPartnerId ? (
          <div style={{
            padding: '40px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <p style={{ color: '#6b7280', margin: 0 }}>Please select a partner to manage referral settings</p>
          </div>
        ) : (
          <>
        
        {/* Tab Navigation */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px 8px 0 0',
          borderBottom: 'none',
          display: 'flex',
          gap: '8px',
          padding: '0 20px'
        }}>
          <div
            onClick={() => handleTabChange('settings')}
            style={activeTab === 'settings' ? activeTabStyle : tabStyle}
          >
            Settings
          </div>
          <div
            onClick={() => handleTabChange('stats')}
            style={activeTab === 'stats' ? activeTabStyle : tabStyle}
          >
            Stats
          </div>
          <div
            onClick={() => handleTabChange('referrals')}
            style={activeTab === 'referrals' ? activeTabStyle : tabStyle}
          >
            Referrals
          </div>
        </div>

        {/* Tab Content */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '0 0 8px 8px',
          padding: '24px',
          minHeight: '400px'
        }}>
        
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            {/* Warning Banner when no settings in DB */}
            {!hasSettingsInDb && (
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '16px 20px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'start',
                gap: '12px'
              }}>
                <div style={{ fontSize: '24px' }}>⚠️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                    No Settings Configured
                  </div>
                  <div style={{ color: '#78350f', fontSize: '14px' }}>
                    This partner does not have referral settings saved in the database. 
                    The form below shows default values. You must click "Save Settings" to apply these settings.
                    {settingsModified && (
                      <strong style={{ display: 'block', marginTop: '4px' }}>
                        ⚠️ You have unsaved changes!
                      </strong>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Reset to Defaults Button */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginBottom: '16px' 
            }}>
              <button
                onClick={handleResetToDefaults}
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Reset to Defaults
              </button>
            </div>
            
            {/* Referral Limits */}
            <div style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ marginBottom: '16px' }}>Referral Limits</h3>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Set to 0 for unlimited. These limits apply per user.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Total Referrals
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.limits.totalReferrals}
                    onChange={(e) => handleLimitChange('totalReferrals', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px' 
                    }}
                  />
                  <small style={{ color: '#666' }}>Lifetime limit per user</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Monthly Referrals
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.limits.monthlyReferrals}
                    onChange={(e) => handleLimitChange('monthlyReferrals', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px' 
                    }}
                  />
                  <small style={{ color: '#666' }}>Max per calendar month</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Concurrent Referrals
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.limits.concurrentReferrals}
                    onChange={(e) => handleLimitChange('concurrentReferrals', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px' 
                    }}
                  />
                  <small style={{ color: '#666' }}>Active at same time</small>
                </div>
              </div>
            </div>
            
            {/* Points Configuration */}
            <div style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ marginBottom: '16px' }}>Points Rewards</h3>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Configure points awarded for referrals and expiration rules.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Referrer Points
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.points.referrerSignupPoints}
                    onChange={(e) => handlePointsChange('referrerSignupPoints', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px' 
                    }}
                  />
                  <small style={{ color: '#666' }}>Points for referring someone</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Referred User Points
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.points.referredSignupPoints}
                    onChange={(e) => handlePointsChange('referredSignupPoints', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px' 
                    }}
                  />
                  <small style={{ color: '#666' }}>Welcome bonus for referred user</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Points Expiration (Days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.points.expirationDays}
                    onChange={(e) => handlePointsChange('expirationDays', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px' 
                    }}
                  />
                  <small style={{ color: '#666' }}>0 = never expire</small>
                </div>
              </div>
            </div>
            
            {/* Node Types */}
            <div style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Network Node Types</h3>
                <button
                  onClick={handleAddNodeType}
                  style={{
                    padding: '8px 16px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + Add Type
                </button>
              </div>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Define relationship types for network categorization. Users can label their connections privately.
              </p>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                {settings.nodeTypes.map((nodeType, index) => (
                  <div 
                    key={index}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '12px',
                      background: '#f7fafc',
                      borderRadius: '6px'
                    }}
                  >
                    <div>
                      <strong>{nodeType.label}</strong> <code style={{ color: '#666' }}>({nodeType.id})</code>
                      {nodeType.description && (
                        <div style={{ fontSize: '14px', color: '#666' }}>{nodeType.description}</div>
                      )}
                      {nodeType.isDefault && (
                        <span style={{ fontSize: '12px', color: '#667eea' }}>Default</span>
                      )}
                    </div>
                    {!nodeType.isDefault && (
                      <button
                        onClick={() => handleRemoveNodeType(index)}
                        style={{
                          padding: '4px 12px',
                          background: '#fee',
                          color: '#c33',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Other Settings */}
            <div style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ marginBottom: '16px' }}>Other Settings</h3>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Redirect URL After Signup
                </label>
                <input
                  type="url"
                  value={settings.redirectUrl}
                  onChange={(e) => {
                    setSettings(prev => ({ ...prev, redirectUrl: e.target.value }));
                    setSettingsModified(true);
                  }}
                  placeholder="https://yoursite.com/welcome"
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '4px' 
                  }}
                />
                <small style={{ color: '#666' }}>Where to redirect users after referral signup</small>
              </div>
              
              {/* CNXN Integration Webhook Settings */}
              <div style={{ 
                marginBottom: '16px',
                padding: '16px',
                background: '#f8fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                  🔗 Partner Integration Webhook (White-Label Referrals)
                </h4>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                  Configure webhook to send invite codes to partner backend for white-label email delivery
                </p>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={settings.webhookUrl || ''}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, webhookUrl: e.target.value }));
                      setSettingsModified(true);
                    }}
                    placeholder="https://partner.com/api/webhook/cnxn/referral-invite"
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                  <small style={{ color: '#64748b', fontSize: '12px' }}>
                    Endpoint to receive invite codes after generation
                  </small>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                    Webhook Secret
                  </label>
                  <input
                    type="text"
                    value={settings.webhookSecret || ''}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, webhookSecret: e.target.value }));
                      setSettingsModified(true);
                    }}
                    placeholder="shared_secret_for_hmac_signature"
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: 'monospace'
                    }}
                  />
                  <small style={{ color: '#64748b', fontSize: '12px' }}>
                    Used for HMAC-SHA256 signature verification (keep secure)
                  </small>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                    Custom SSR URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={settings.ssrUrl || ''}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, ssrUrl: e.target.value }));
                      setSettingsModified(true);
                    }}
                    placeholder="https://partner.com"
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                  <small style={{ color: '#64748b', fontSize: '12px' }}>
                    Override SSR_URL env variable for this partner only
                  </small>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.enableProxy !== false}
                      onChange={(e) => {
                        setSettings(prev => ({ ...prev, enableProxy: e.target.checked }));
                        setSettingsModified(true);
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ fontSize: '13px', fontWeight: '500' }}>Enable Proxy Routing</span>
                  </label>
                  <small style={{ color: '#64748b', fontSize: '12px', marginLeft: '24px', display: 'block', marginTop: '4px' }}>
                    Use custom domain URLs (e.g., partner.com/invite/CODE) instead of direct URLs (x.cnxn.com/r/CODE). 
                    Requires Custom SSR URL and reverse proxy configuration.
                  </small>
                </div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings.enableNetworkVisualization}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, enableNetworkVisualization: e.target.checked }));
                      setSettingsModified(true);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Enable Network Visualization (Phase 2 Feature)</span>
                </label>
              </div>
              
              <div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings.enableNodeTypes}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, enableNodeTypes: e.target.checked }));
                      setSettingsModified(true);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Enable Node Type Selection for Users</span>
                </label>
              </div>
            </div>
            
            {/* Save Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                style={{
                  padding: '12px 32px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              {!hasSettingsInDb && (
                <span style={{ color: '#dc2626', fontSize: '14px', fontWeight: '500' }}>
                  ⚠️ Not saved to database
                </span>
              )}
              {hasSettingsInDb && settingsModified && (
                <span style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '500' }}>
                  • Unsaved changes
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>{stats.totalReferrals || 0}</div>
                <div style={{ color: '#666' }}>Total Referrals</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#48bb78' }}>{stats.byStatus?.completed || 0}</div>
                <div style={{ color: '#666' }}>Completed</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ed8936' }}>{stats.byStatus?.pending || 0}</div>
                <div style={{ color: '#666' }}>Pending</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>{stats.networkSize || 0}</div>
                <div style={{ color: '#666' }}>Network Connections</div>
              </div>
            </div>
            
            {/* Top Referrers */}
            <div style={{ background: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '16px' }}>Top Referrers</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Referrals</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topReferrers.map((referrer, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>{referrer.name}</td>
                      <td style={{ padding: '12px' }}>{referrer.email}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{referrer.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: '16px' }}>Recent Referrals</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Referrer</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Referred</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Organization</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentReferrals.map((ref) => (
                  <tr key={ref.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px' }}>
                      <div>{ref.referrerName}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{ref.referrerEmail}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>{ref.referredName}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{ref.referredEmail}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        background: ref.status === 'completed' ? '#c6f6d5' : 
                                   ref.status === 'pending' ? '#fed7d7' : '#e2e8f0',
                        color: ref.status === 'completed' ? '#22543d' : 
                               ref.status === 'pending' ? '#742a2a' : '#4a5568'
                      }}>
                        {ref.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{ref.organizationName || '-'}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>
                      {new Date(ref.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralManagementPage;